import { managementReportFilterGridSizes } from "@common/components/managementReport/managementReportTableSection/ManagementReportTableSection";
import React from "react";
import { useTranslation } from "../../../localization/useTranslation";
import { LegalEntityAuditGroupAuditModel, LegalEntityAuditGroupAuditTableModel } from "../../legalEntityAuditGroupOverview/LegalEntityAuditGroupOverview.types";
import { useManagementReportCompletedAudits } from "./ManagementReportCompletedAudits.hooks";
import { useUrlProvider } from "../../../useUrlProvider";
import { DotLegalMultiSelect, DotLegalTooltip, ITableHeader } from "@dotlegal/dotlegal-ui-components";
import { Box, TableCell, TableRow } from "@mui/material";
import Grid from "@mui/material/Grid2";
import QuestionnaireReportOverflowButtons from "../../../common/components/questionnaireComponents/questionnaireReportButtons/QuestionnaireReportOverflowButtons";
import QuestionnaireReportButtons from "../../../common/components/questionnaireComponents/questionnaireReportButtons/QuestionnaireReportButtons";
import DotLegalLink from "../../../common/components/dotLegalLink/DotLegalLink";
import SystemLinks from "../../../system/SystemLinks";
import { getQuestionnaireResponseScore } from "../../../common/questionnaireResponseScore";
import { LegalEntityAuditStatus } from "../../../legalEntity/legalEntityAuditTab/LegalEntityAudit.types";
import { LegalEntityContactType } from "../../../legalEntity/legalEntityContactTab/LegalEntityContactTab.types";
import { setFirstLetterToLowerCase } from "../../../common/stringOperations";
import { IDownloadAuditReportModel } from "../../DowloadLegalEntityAuditReport.hooks";
import QuestionnaireRisk from "../../../common/components/questionnaireComponents/questionnaireRisk/QuestionnaireRisk";
import QuestionnaireEvaluation from "../../../common/components/questionnaireComponents/questionnaireEvaluation/QuestionnaireEvaluation.tsx";
import { LegalEntityAuditTabs } from "../../../legalEntity/LegalEntity.types.ts";
import MentionInputRender from "../../../common/components/mentionInput/MentionInputRender.tsx";
import { useLegalEntityTypeNames } from "../../../legalEntity/useLegalEntityTypeNames.ts";
import ManagementReportTableSection from "@common/components/managementReport/managementReportTableSection/ManagementReportTableSection";

export interface ManagementReportCompletedAuditsProps {
    isLoading: boolean;
    audits: Array<LegalEntityAuditGroupAuditModel>;
    scoreEnabled: boolean;
    isPdf: boolean;
}

function ManagementReportCompletedAudits(props: ManagementReportCompletedAuditsProps) {
    const { translateString, translateLegalEntityAuditStatus, translateDate } = useTranslation();
    const { getLegalEntityTypeName } = useLegalEntityTypeNames();
    const {
        rows,
        searchedRiskLevel,
        setSearchedRiskLevel,
        onDownloadReportAsPdf,
        getReportPdfUrl,
        isDownloadingPdf,
        riskLevelFilterOptions,
        searchedAuditScore,
        setSearchedAuditScore,
        auditScoreFilterOptions,
        auditEvaluationFilterOptions,
        searchedAuditEvaluation,
        setSearchedAuditEvaluation,
    } = useManagementReportCompletedAudits(props);
    const { getLegalEntityUrl } = useUrlProvider();

    function getHeaders() {
        let headers = Array<ITableHeader<LegalEntityAuditGroupAuditTableModel>>();
        headers.push({ property: "legalEntityName", text: translateString("legalEntities"), align: "left", showOnMobile: true });
        headers.push({ property: "assets", text: translateString("assets"), align: "left", showOnMobile: true });
        headers.push({ property: "contact", text: translateString("contact"), align: "left", showOnMobile: true });
        headers.push({ property: "riskLevel", text: translateString("riskLevel"), align: "left", showOnMobile: true });
        headers.push({ property: "score", text: translateString("score"), align: "left", showOnMobile: true });
        headers.push({ property: "evaluationStatus", text: translateString("evaluation"), align: "left", showOnMobile: true });

        if (props.isPdf) {
            headers.push({ property: "evaluation", text: translateString("evaluationText"), align: "left", showOnMobile: true });
        }

        headers.push({ property: "report", text: translateString("legalEntityAuditReport"), align: "left", showOnMobile: true });

        // Hidden headers - allowing search
        headers.push({ property: "assetsString", text: "", align: "left", showOnMobile: true, hideHeader: true });
        headers.push({ property: "contactTypesString", text: "", align: "left", showOnMobile: true, hideHeader: true });
        headers.push({ property: "auditStatusString", text: "", align: "left", showOnMobile: true, hideHeader: true });

        return headers;
    }

    function getSearchFields() {
        if (rows.some((x) => !x.scoreEnabled)) {
            return undefined;
        }

        return (
            <React.Fragment>
                <Grid size={managementReportFilterGridSizes}>
                    <DotLegalMultiSelect
                        onChange={setSearchedRiskLevel}
                        chosenOptions={searchedRiskLevel}
                        label={translateString("riskLevel")}
                        placeholder={translateString("riskLevel")}
                        isLoading={props.isLoading}
                        options={riskLevelFilterOptions}
                        noMargin
                        limit={1}
                        noOptionsLabel={translateString("noOptions")}
                    />
                </Grid>
                <Grid size={managementReportFilterGridSizes}>
                    <DotLegalMultiSelect
                        onChange={setSearchedAuditScore}
                        chosenOptions={searchedAuditScore}
                        label={translateString("score")}
                        placeholder={translateString("score")}
                        isLoading={props.isLoading}
                        options={auditScoreFilterOptions}
                        noMargin
                        limit={1}
                        noOptionsLabel={translateString("noOptions")}
                    />
                </Grid>
                <Grid size={managementReportFilterGridSizes}>
                    <DotLegalMultiSelect
                        onChange={setSearchedAuditEvaluation}
                        chosenOptions={searchedAuditEvaluation}
                        label={translateString("evaluation")}
                        placeholder={translateString("evaluation")}
                        isLoading={props.isLoading}
                        options={auditEvaluationFilterOptions}
                        noMargin
                        limit={1}
                        noOptionsLabel={translateString("noOptions")}
                    />
                </Grid>
            </React.Fragment>
        );
    }

    function getReportButtons(row: LegalEntityAuditGroupAuditTableModel) {
        const downloadModel: IDownloadAuditReportModel = {
            legalEntityAuditId: row.id,
            deadline: row.deadline,
            legalEntityName: row.legalEntityName,
            questionnaireName: row.questionnaireName,
            questionnaireResponseId: row.questionnaireResponseId,
            responseExternalId: row.responseExternalId,
            sender: row.sender,
            hasRecommendations: row.hasRecommendations,
        };
        const reportUrl = getReportPdfUrl(row.questionnaireResponseId, row.deadline, row.sender, row.legalEntityName, false, row.hasRecommendations);
        const showButtons = row.status !== LegalEntityAuditStatus.Opened && row.status !== LegalEntityAuditStatus.Sent && !row.closed;
        const downloadingPdf = isDownloadingPdf(row.id);

        if (row.scoreEnabled || row.hasRecommendations) {
            return (
                <QuestionnaireReportOverflowButtons
                    reportPdfUrl={reportUrl}
                    reportPdfMenuItems={getReportButtonMenuItems(row.scoreEnabled, downloadModel)}
                    showButtons={showButtons}
                    isDownloadingPdf={downloadingPdf}
                />
            );
        }

        return (
            <QuestionnaireReportButtons
                isDownloadingPdf={downloadingPdf}
                onDownloadReportPdf={() => onDownloadReportAsPdf(downloadModel, true, row.hasRecommendations)}
                reportPdfUrl={reportUrl}
                showButtons={showButtons}
            />
        );
    }

    function getReportButtonMenuItems(isScoreEnabled: boolean, row: IDownloadAuditReportModel) {
        if (isScoreEnabled) {
            return [
                {
                    menuItem: row.hasRecommendations ? translateString("downloadWithScoreAndRecommendations") : translateString("downloadWithScore"),
                    onClick: () => onDownloadReportAsPdf(row, false, row.hasRecommendations),
                },
                {
                    menuItem: row.hasRecommendations ? translateString("downloadWithoutScoreAndRecommendations") : translateString("downloadWithoutScore"),
                    onClick: () => onDownloadReportAsPdf(row, true, false),
                },
            ];
        } else {
            return [
                { menuItem: translateString("downloadWithRecommendations"), onClick: () => onDownloadReportAsPdf(row, false, true) },
                {
                    menuItem: translateString("downloadWithoutRecommendations"),
                    onClick: () => onDownloadReportAsPdf(row, false, false),
                },
            ];
        }
    }

    return (
        <ManagementReportTableSection
            title={translateString("completed")}
            count={props.audits.length}
            isPdf={props.isPdf}
            headers={getHeaders()}
            defaultOrderBy={"legalEntityName"}
            data={mapToTableData(rows)}
            searchFields={getSearchFields()}
            headerMarginBottom={props.scoreEnabled ? 2 : props.isPdf ? 0 : -3.75}
            renderRow={(row) => {
                return (
                    <TableRow key={row.legalEntityId}>
                        <TableCell>
                            <Box key={row.id} component={"span"} sx={{ fontWeight: "bold" }}>
                                <DotLegalLink linkColor={"primary"} to={`${getLegalEntityUrl(row.legalEntityId, LegalEntityAuditTabs.Audits)}`}>
                                    {row.legalEntityName}
                                </DotLegalLink>
                            </Box>
                        </TableCell>

                        <TableCell>
                            <SystemLinks systems={row.assets} />
                        </TableCell>
                        <TableCell>
                            <DotLegalTooltip text={row.contactEmail}>
                                <Box>
                                    {row.contact} {row.contactTypes.length > 0 && <>({row.contactTypesString})</>}
                                </Box>
                            </DotLegalTooltip>
                        </TableCell>
                        <TableCell>
                            <QuestionnaireRisk riskLevel={row.riskLevel} riskName={row.riskName} scoreEnabled={row.scoreEnabled} status={row.status} />
                        </TableCell>
                        <TableCell align="right">{row.scoreString}</TableCell>
                        <TableCell>
                            <QuestionnaireEvaluation evaluationStatus={row.evaluationStatus} evaluation={row.evaluation} />
                        </TableCell>
                        {props.isPdf && <TableCell>{row.evaluation}</TableCell>}
                        <TableCell>{getReportButtons(row)}</TableCell>
                    </TableRow>
                );
            }}
        />
    );

    function mapToTableData(audits: Array<LegalEntityAuditGroupAuditModel> | undefined): Array<LegalEntityAuditGroupAuditTableModel> {
        if (audits) {
            return audits.map((x) => {
                return {
                    ...x,
                    assetsString: x.assets.flatMap((x) => x.name).join(", "),
                    contactTypesString: x.contactTypes.map((x) => translateString(setFirstLetterToLowerCase(LegalEntityContactType[x].toString()))).join(", "),
                    auditStatusString: translateLegalEntityAuditStatus(x.status.toString()),
                    scoreString: getQuestionnaireResponseScore(
                        x.status === LegalEntityAuditStatus.Submitted,
                        x.scoreEnabled,
                        x.score,
                        translateString("scoreNotEnabled")
                    ),
                    legalEntityTypeString: x.legalEntityTypes.map((type) => getLegalEntityTypeName(type)).join(", "),
                    reminderSentString: x.reminderSent ? translateDate(x.reminderSent) : "",
                };
            });
        }

        return [];
    }
}

export default ManagementReportCompletedAudits;
